import React, { useState, useEffect } from 'react'
import { Classes, Icon, Popover, PopoverInteractionKind, Position } from '@blueprintjs/core'
import { Dialog, SaveButton, CancelButton, Spinner, FormRadioGroup, Card, Button } from '../../Elements'

import { extractGAddress, buildGeocodeUrl } from '../../utils/helpers'

import { gql } from '@apollo/client'
import { useLazyQuery } from '../../Hooks/graphql_utils'
import { PotentialDuplicateEstimateCard } from '../Estimate/EstimateForm'

const CHECK_FOR_DUPLICATES_QUERY = gql`
    query addressVerificationDuplicateCheck ($lat: String, $lon: String) {
        checkDupicatesByAddress(lat: $lat, lon: $lon) {
            id
            name
            company {
                company_name
            }
            address_line_1
            city
            state
            entered_date
        }
    }
`

export const CheckAddressDialog = ({ values, save, cancel, isOpen, setIsOpen, enableCheckForDuplicates = false }) => {
    const [addresses, setAddresses] = useState(null)
    const [selectedAddress, setSelectedAddress] = useState(null)
    const [isDuplicatesWindowOpen, setIsDuplicatesWindowOpen] = useState(false)
    const [duplicates, setDuplicates] = useState([])

    const [loadDuplicates, { loading }] = useLazyQuery(CHECK_FOR_DUPLICATES_QUERY, {
        onCompleted: (data) => {
            setDuplicates(data?.checkDupicatesByAddress)
            if (data?.checkDupicatesByAddress?.length > 0) setIsDuplicatesWindowOpen(true)
        }
    })

    const checkDuplicatesAndSave = async () => {
        if (enableCheckForDuplicates) {
            loadDuplicates({ variables: { lat: selectedAddress?.lat?.toString(), lon: selectedAddress?.lon?.toString() } })
        }
        setIsOpen(false)
        save(selectedAddress)
    }

    useEffect(() => {
        async function fetchAddresses() {
            if (isOpen) {
                const url = buildGeocodeUrl(values.address_line_1, values.city, values.state, values.county, values.zip_code)
                
                const response = await fetch(url)
                const results = await response.json()
                setAddresses(results.results)
            }
        }

        fetchAddresses()

    }, [values, setAddresses, isOpen])

    const mappedAddresses = (addresses || [])
        .map(address => extractGAddress(address))
        .filter((a, idx, self) => {
            return self.findIndex((x) => x.name === a.name) === idx;
        });

    const InfoPopover = () => {
        const [isOpen, setIsOpen] = useState(false)
        return <Popover
            interactionKind={PopoverInteractionKind.CLICK}
            position={Position.BOTTOM}
            content={<Card style={{ margin: 0 }}><p>Based on the address you just selected, here are some Potential Duplicate Estimates that already exist.</p></Card>}
            isOpen={isOpen}
            onInteraction={(state) => setIsOpen(state)}
            children={
                <Icon icon='info-sign' intent='primary' style={{ display: 'inline-block', verticalAlign: 'middle' }} />
            }
        />
    }

    return (<div>
        <Dialog
            title='Potential Duplicates Detected'
            isOpen={isDuplicatesWindowOpen}
            canOutsideClickClose={false}
            canEscapeKeyClose={false}
            isCloseButtonShown={false}
        >
            <div className={Classes.DIALOG_BODY}>
                <Card>
                    {loading && <Spinner />}
                    {duplicates?.length > 0 && <> {duplicates.map(r => {
                        return <PotentialDuplicateEstimateCard potentialDuplicateEstimate={r} />
                    })}
                    </>}
                </Card>
            </div>

            <div className={Classes.DIALOG_FOOTER}>
                <div className={Classes.DIALOG_FOOTER_ACTIONS} style={{ marginRight: '10px', justifyContent: 'center' }}>
                    <InfoPopover />
                    <Button intent='primary' text='Continue' onClick={() => setIsDuplicatesWindowOpen(false)} />
                </div>
            </div>
        </Dialog>
        <Dialog
            icon='map-marker'
            title='Address Verification'
            isOpen={isOpen}
            canOutsideClickClose={false}
            canEscapeKeyClose={false}
            isCloseButtonShown={false}
        >
            <div className={Classes.DIALOG_BODY}>
                <Card>
                    {!addresses && <Spinner />}
                    {addresses && addresses.length > 0 &&
                        <FormRadioGroup
                            value={selectedAddress}
                            label='Addresses'
                            items={mappedAddresses}
                            onChange={(evt) => {
                                // eslint-disable-next-line
                                const address = mappedAddresses.find(a => a.id == evt.target.value)
                                setSelectedAddress(address)
                            }}
                        />}
                    {addresses && addresses.length === 0 && 'No matching addresses'}
                </Card>
            </div>

            <div className={Classes.DIALOG_FOOTER}>
                <div className={Classes.DIALOG_FOOTER_ACTIONS} style={{ marginRight: '10px' }}>
                    <SaveButton onClick={() => checkDuplicatesAndSave()} />
                    <CancelButton onClick={cancel} />
                </div>
            </div>
        </Dialog>
    </div>
    )
}